import type { ReactNode } from "react"
import { forwardRef } from "react"

import { cn } from "../lib/utils"
import type { ButtonProps as BaseButtonProps } from "../shadcnBase/ShadcnButton"
import { Button as BaseButton, buttonVariants } from "../shadcnBase/ShadcnButton"
import { Loader } from "./Loader"

export type ButtonProps = BaseButtonProps & {
  loading?: boolean
  leftIcon?: ReactNode
  loaderIconSize?: number
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, className, loading, leftIcon, disabled, loaderIconSize, ...p }, ref) => {
    // asChild → BaseButton renders a Radix Slot, which requires EXACTLY ONE child
    // (React.Children.only). Injecting the loader/leftIcon siblings here would crash it,
    // so when asChild is set we pass the single child straight through — the caller's
    // element owns its own content. (loading/leftIcon are no-ops in asChild mode.)
    if (p.asChild) {
      return (
        <BaseButton className={cn("gap-1", className)} ref={ref} disabled={disabled || loading} {...p}>
          {children}
        </BaseButton>
      )
    }
    return (
      <BaseButton className={cn("gap-1", className)} ref={ref} disabled={disabled || loading} {...p}>
        {loading && <Loader className="pointer-events-none" size={loaderIconSize} />}

        {!loading && !!leftIcon && <div className="pointer-events-none">{leftIcon}</div>}
        {children}
      </BaseButton>
    )
  },
)

Button.displayName = "aix-button"

export { buttonVariants }
